'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { useApiQuery } from '@/hooks/use-api'
import { useOptionalCurrentBrand } from '@/hooks/use-current-brand'
import { CONVERSATION_LIST_DIRTY_EVENT } from '@/lib/chat-history-events'
import { getNotificationPreferences } from '@/lib/notification-preferences-actions'
import { getCurrentSubscription } from '@/lib/push-subscription'

type NotificationListItem = {
  id: string
  type:
    | 'automation_run_done'
    | 'automation_run_error'
    | 'action_review_ready'
    | 'agent_turn_complete'
    | 'out_of_credits'
    | 'ad_review_alert'
    | 'chatgpt_ads_low_balance'
    | 'chatgpt_ads_balance_depleted'
    | 'chatgpt_ads_balance_recovered'
    | 'chatgpt_ads_monthly_statement'
  titleKey: string
  titleParams: Record<string, unknown> | null
  payload: Record<string, unknown> | null
  readAt: string | null
  createdAt: string
}

type NotificationListPage = {
  items: NotificationListItem[]
}

/**
 * Fire an in-app toast + (optional) browser system notification each
 * time a new notification row arrives.
 *
 * - Polls ``/api/notifications`` every 15 s (SWR pauses on hidden tabs)
 * - First poll seeds the "last seen" cursor so existing rows from
 *   before the user opened the app do not all toast at once.
 * - Browser Notification API fires only when permission is ``granted``
 *   — we never ask unprompted; the bell popover surfaces the prompt.
 * - Sonner toast is exposed via ``onClick`` so the caller can route to
 *   the right deep link without this hook knowing the routing rules.
 */
/**
 * Track whether SW-backed push is delivering for this browser.
 *
 * "Active" means BOTH the server has push_enabled=true AND this browser
 * has a live pushManager subscription. When both are true the SW will
 * deliver the OS notification and the hook below MUST NOT also fire
 * ``new window.Notification(...)`` — otherwise users see two toasts
 * per event (Phase 2 finding #C4 in plan.md).
 *
 * Recomputed on visibilitychange + focus so cross-tab subscription
 * changes (user enabled in /settings on another tab) are noticed
 * before the next 15s notification poll.
 */
// Throttle: a heavy tab-switcher fires ``focus`` many times per minute.
// 30s is short enough that enabling push in another tab is reflected
// before the next toast attempt, but long enough that we don't pay an
// authenticated round-trip on every focus event.
const PUSH_STATE_REFRESH_MS = 30_000

/** Custom event dispatched by /settings/notifications when the user
 *  toggles push in this tab — bypasses the 30s throttle so usePushActive
 *  picks up the change before the next toast attempt and avoids the
 *  double-fire window (Codex PR #1642 P2). */
export const PUSH_STATE_CHANGED_EVENT = 'nex:push-state-changed'

function usePushActive(): boolean | null {
  // ``null`` = not yet resolved. Toast hook treats both ``true`` and
  // ``null`` as "suppress browser-API fallback" so the SW + Notification
  // double-fire window doesn't open between mount and the first check
  // resolving (Bugbot PR #1642 BB6). Once resolved we settle on a
  // strict ``true``/``false``.
  //
  // Initial sync narrowing: if Notification.permission isn't granted,
  // push can't possibly be active → start at ``false`` and skip the
  // round-trip entirely.
  const initial =
    typeof window !== 'undefined' && 'Notification' in window
      ? Notification.permission === 'granted'
        ? null
        : false
      : false
  const [active, setActive] = React.useState<boolean | null>(initial)
  const lastCheckedAtRef = React.useRef<number>(0)

  React.useEffect(() => {
    let cancelled = false
    const check = async (force = false) => {
      if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        if (!cancelled) setActive(false)
        return
      }
      const now = Date.now()
      if (!force && now - lastCheckedAtRef.current < PUSH_STATE_REFRESH_MS) {
        return
      }
      lastCheckedAtRef.current = now
      try {
        const sub = await getCurrentSubscription()
        // Skip the authenticated prefs round-trip when this browser
        // has no local subscription — push can't possibly be active.
        if (sub === null) {
          if (!cancelled) setActive(false)
          return
        }
        const prefs = await getNotificationPreferences()
        if (!cancelled) setActive(prefs.pushEnabled === true)
      } catch {
        if (!cancelled) setActive(false)
      }
    }
    void check(true)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check()
    }
    const onFocus = () => {
      void check()
    }
    const onSameTabChange = () => {
      void check(true) // force — bypass throttle for explicit user action
    }
    // ``visibilitychange`` is dispatched on ``document``, not ``window``
    // (Codex PR #1642 P3). Some browsers don't surface it on window.
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    window.addEventListener(PUSH_STATE_CHANGED_EVENT, onSameTabChange)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener(PUSH_STATE_CHANGED_EVENT, onSameTabChange)
    }
  }, [])

  return active
}

export function useNotificationToasts(opts: {
  onItemClick: (item: NotificationListItem) => void
}): void {
  const { onItemClick } = opts
  const t = useTranslations('Notifications.title')
  const tBell = useTranslations('Notifications.bell')
  const pushActive = usePushActive()

  // Stores the highest ``createdAt`` already surfaced as a toast so the
  // hook never double-fires on revalidate or focus events.
  const lastSeenAtRef = React.useRef<string | null>(null)

  // Scope the toast poll to the active brand so /b/{a} toasts don't pop
  // when a notification belongs to /b/{b}. Outside a brand layout the
  // scope is unset and the legacy unscoped feed is used.
  const brand = useOptionalCurrentBrand()
  const brandSlug = brand?.brandSlug ?? null
  const { data } = useApiQuery<NotificationListPage>('/api/notifications', {
    query: brandSlug ? { limit: '5', brand: brandSlug } : { limit: '5' },
    refreshInterval: 15_000,
    revalidateOnFocus: true,
    keepPreviousData: true,
  })

  // Reset the cursor when the active brand changes. The SWR key now
  // includes the brand slug, so a brand switch fetches a different
  // notification set whose ``createdAt`` values are unrelated to the
  // previous brand's cursor. Without this reset the next poll for the
  // new brand could burst-toast everything the old cursor predates
  // (Bugbot PR #1718 — toast cursor on brand switch).
  React.useEffect(() => {
    lastSeenAtRef.current = null
  }, [brandSlug])

  React.useEffect(() => {
    if (!data || data.items.length === 0) return

    const newest = data.items[0]?.createdAt ?? null
    const cursor = lastSeenAtRef.current

    // Items not yet observed on a previous poll. On the first non-empty poll
    // the cursor is null and every row counts as newly observed.
    const newlyObserved =
      cursor === null
        ? data.items
        : data.items.filter(item => item.createdAt > cursor)

    // A background automation run creates a conversation that has no live feed
    // into the chat-history list. Signal it to reconcile whenever a newly
    // observed automation completion appears — derived from the raw items, not
    // the toast-gated ``fresh`` set below. Deriving it here (before the cursor
    // seed return and without the read filter) is what lets the first automation
    // of a fresh session, or a row already read via the bell, still refresh the
    // chat list; reconcileFirstPage is idempotent so a redundant signal is safe.
    if (
      newlyObserved.some(
        item =>
          item.type === 'automation_run_done' ||
          item.type === 'automation_run_error',
      )
    ) {
      window.dispatchEvent(new Event(CONVERSATION_LIST_DIRTY_EVENT))
    }

    // First non-empty poll: seed cursor only. Without this the user
    // would get a toast burst on every page load for items they
    // already knew about.
    if (cursor === null) {
      lastSeenAtRef.current = newest
      return
    }

    // Skip items the user already saw via the popover (read_at set) —
    // otherwise a quick "open bell → close bell" before the next poll
    // would still trigger a toast burst the next tick.
    const fresh = data.items.filter(
      item => item.createdAt > cursor && item.readAt === null,
    )
    lastSeenAtRef.current = newest
    if (fresh.length === 0) return

    // Process oldest-first so the most recent toast lands on top of
    // the sonner stack.
    for (const item of [...fresh].reverse()) {
      const key = item.titleKey.replace(/^notifications\.title\./, '')
      // next-intl's typed wrapper rejects dynamic keys; the catalog is
      // the source of truth so we cast to the loose signature.
      const looseT = t as unknown as (
        k: string,
        p: Record<string, string | number>,
      ) => string
      const params = (item.titleParams ?? {}) as Record<string, string | number>
      const title = looseT(key, params)

      // In-app toast — clickable, dismisses on selection.
      toast(title, {
        id: item.id,
        action: {
          label: tBell('viewAll'),
          onClick: () => onItemClick(item),
        },
      })

      // Browser system notification — surfaces in the OS notification
      // center when the tab is in the background. Requires explicit
      // permission, which we never ask for from this hook.
      //
      // Skip ONLY when SW-backed push is confirmed active for this
      // browser (pushActive === true). When unknown (null, during the
      // brief mount-time check) OR known inactive (false), fire the
      // Notification API fallback so a user with granted permission
      // but a dead/missing SW subscription still gets an OS-level
      // alert (Bugbot PR #1642 BB12 — silent failure is worse than a
      // brief double-fire window during init).
      if (
        pushActive !== true &&
        typeof window !== 'undefined' &&
        'Notification' in window &&
        window.Notification.permission === 'granted'
      ) {
        try {
          const native = new window.Notification(title, {
            tag: item.id, // dedup if the same id somehow fires twice
            silent: false,
          })
          native.onclick = () => {
            window.focus()
            onItemClick(item)
            native.close()
          }
        } catch {
          // Some browsers (Safari without user gesture, iOS) throw
          // on construction; the toast above already covered the
          // surface.
        }
      }
    }
  }, [data, onItemClick, t, tBell, pushActive])
}
